<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="markdown-preview"
    v-html="htmlContent"
  />
</template>

<script>
import { marked } from 'marked'

/**
 * Custom marked renderer to wrap images and tables with specific HTML structures.
 */
const renderer = new marked.Renderer()
renderer.image = (href, title, text) => {
  const imgStr = `<img src="${href}" alt="${text}"${title ? ` title="${title}"` : ''} />`
  return title ? `<figure class="image-figure">${imgStr}<figcaption class="image-caption">${title}</figcaption></figure>` : imgStr
}

renderer.table = (header, body) => {
  const tableBody = body ? `<tbody>${body}</tbody>` : body
  return `<div class="table-wrapper">
    <table>
      <thead>
        ${header}
      </thead>
      ${tableBody}
    </table>
  </div>`
}

renderer.tablecell = (content, flags) => {
  const cell = (content || '').replace(
    /<figure[^>]*>.*?<img[^>]+src=["']([^"']+)["'][^>]*>.*?<\/figure>|<img[^>]+src=["']([^"']+)["'][^>]*\/>|<\/?(?:pre|code)[^>]*>/gi,
    (match, url1, url2) => url1 || url2 || '',
  )
  const tag = flags.header ? 'th' : 'td'
  const align = flags.align ? ` align="${flags.align}"` : ''
  return `<${tag}${align}>${cell}</${tag}>\n`
}

export default {
  name: 'EditorPreview',
  props: {
    content: {
      type: String,
      default: '',
    },
  },
  computed: {
    htmlContent() {
      return marked.parse(this.content || '', { renderer })
    },
  },
}
</script>

<style scoped>
.markdown-preview {
  font-family: inherit;
  color: #1F2937;
  line-height: 1.6;
  overflow-wrap: anywhere;
  word-break: normal;
  max-width: 100%;
}
.markdown-preview ::v-deep h1 {
  color: #09090B;
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep h2 {
  color: #09090B;
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
}
.markdown-preview ::v-deep h3 {
  color: #09090B;
  font-size: 1.125rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}
.markdown-preview ::v-deep p {
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep ul {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep ol {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep strong,
.markdown-preview ::v-deep b {
  font-weight: bold;
}
.markdown-preview ::v-deep em {
  font-style: italic;
}
.markdown-preview ::v-deep blockquote {
  border-left: 4px solid #E5E7EB;
  padding-left: 1rem;
  color: #4B5563;
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep img {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep .image-figure {
  margin: 0 0 1rem 0;
  text-align: center;
}
.markdown-preview ::v-deep .image-figure img {
  margin-bottom: 0.5rem;
}
.markdown-preview ::v-deep .image-caption {
  font-size: 0.875rem;
  color: #6B7280;
  font-style: italic;
}
.markdown-preview ::v-deep code {
  background-color: #F3F4F6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875em;
}
.markdown-preview ::v-deep pre {
  background-color: #1F2937;
  color: #F9FAFB;
  padding: 1rem;
  border-radius: 0.375rem;
  overflow-x: auto;
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep pre code {
  background-color: transparent;
  color: inherit;
  padding: 0;
}
.markdown-preview ::v-deep a {
  color: #2563EB;
  text-decoration: underline;
}
.markdown-preview ::v-deep .table-wrapper {
  overflow-x: auto;
  margin-bottom: 1rem;
}
.markdown-preview ::v-deep table {
  width: 100%;
  border-collapse: collapse;
}
.markdown-preview ::v-deep table th,
.markdown-preview ::v-deep table td {
  border: 1px solid #E5E7EB;
  padding: 0.5rem 0.75rem;
  text-align: left;
}
.markdown-preview ::v-deep table th {
  background-color: #F9FAFB;
  font-weight: 600;
}
.markdown-preview ::v-deep code {
  color: #333333;
}
</style>
